/**
 * Client selector dropdown in the app header.
 * Shows the active client name; clicking opens a dropdown to switch.
 */

import * as React from 'react'
import { Building2, ChevronDown } from 'lucide-react'
import { useClientsList, useActiveClient } from '@/hooks/useClients'
import { cn } from '@/lib/utils'

export function ClientSelector() {
  const { data: clients = [] } = useClientsList()
  const { activeClientId, setActiveClientId } = useActiveClient()
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  const activeClient = clients.find((c) => c.id === activeClientId)

  // Close on outside click
  React.useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
          'bg-background hover:bg-accent hover:text-accent-foreground',
          activeClient ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        <Building2 className="h-4 w-4 shrink-0" />
        <span className="max-w-[160px] truncate">
          {activeClient ? activeClient.name : 'Все клиенты'}
        </span>
        <ChevronDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 min-w-[220px] rounded-md border bg-popover shadow-md py-1">
          {/* All clients option */}
          <button
            onClick={() => { setActiveClientId(null); setOpen(false) }}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent transition-colors',
              !activeClientId ? 'font-semibold text-foreground' : 'text-muted-foreground',
            )}
          >
            <span className="w-2 h-2 rounded-full shrink-0" />
            Все клиенты
          </button>

          {clients.length > 0 && <div className="mx-3 my-1 border-t" />}

          {clients.map((c) => (
            <button
              key={c.id}
              onClick={() => { setActiveClientId(c.id); setOpen(false) }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent transition-colors',
                activeClientId === c.id ? 'font-semibold text-foreground' : 'text-foreground',
              )}
            >
              <span
                className={cn(
                  'w-2 h-2 rounded-full shrink-0',
                  c.status === 'active' ? 'bg-green-500' :
                  c.status === 'prospect' ? 'bg-amber-400' :
                  c.status === 'paused' ? 'bg-slate-400' : 'bg-red-400',
                )}
              />
              <span className="flex-1 truncate">{c.name}</span>
              {c.counts.deals > 0 && (
                <span className="text-xs text-muted-foreground ml-auto pl-2 shrink-0">
                  {c.counts.deals} сд.
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
